import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { AppNotification } from '../models/notification';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 482 — the caller's in-app notification feed for the sidebar. One light poll (15s, matching
// the notification bell) so a new commission / task assignment shows up live; mark-read clears the
// "[new …]" pill. Self-scoped server-side (a user only ever sees their own).
const useNotifications = (enabled = true) => {
  const queryClient = useQueryClient();

  const query = useQuery<AppNotification[]>(
    'notifications',
    async () => {
      const { data } = await axios.get(`${apiUrl()}notification`);
      return Array.isArray(data) ? data : [];
    },
    { enabled, refetchInterval: 30_000, refetchOnWindowFocus: true },
  );

  const notifications = query.data ?? [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markRead = useMutation(
    (id: string) => axios.post(`${apiUrl()}notification/${id}/read`),
    { onSuccess: () => queryClient.invalidateQueries('notifications') },
  );
  const markAllRead = useMutation(
    () => axios.post(`${apiUrl()}notification/read-all`),
    {
      onSuccess: () => {
        // optimistic: clear instantly, next poll confirms
        queryClient.setQueryData<AppNotification[]>('notifications', (prev) => (prev ?? []).map((n) => ({ ...n, isRead: true })));
        queryClient.invalidateQueries('notifications');
      },
    },
  );

  const sendTest = useMutation(
    () => axios.post(`${apiUrl()}notification/test`),
    { onSuccess: () => queryClient.invalidateQueries('notifications') },
  );

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    markRead: markRead.mutateAsync,
    markAllRead: markAllRead.mutateAsync,
    isClearing: markAllRead.isLoading,
    sendTest: sendTest.mutateAsync,
    isSendingTest: sendTest.isLoading,
  };
};

export default useNotifications;
